import notifications from 'notifications';
import {PersistenceInfo} from 'procedures/ProcedurePageContext';
import {
  FlowchartConfigWithMetadataAndRootTaskSchema,
  ProcessFlowchartAdapter,
  ProcessFlowchartAdditionalPersistedData,
} from 'processes/graph-builder-processes/flowchart-viz/horizontal-flowchart/nodes/ProcessFlowchartAdapter';
import {PLUGIN_REGISTRY_INSTANCE} from 'processes/graph-builder-processes/platform/PluginRegistry';

import {ProcessFlowchartNodeType} from '../processFlowchartConfigEditorConstants';
import {getFlowchartConfigSafely} from '../processFlowchartConfigEditorUtils';
import {
  FlowchartConfigWithMetadata,
  setIsFlowchartConfigAutosaverBlocked,
  setSaveInFlightWithError,
} from './flowchartConfigAutosaveManagerCallbacks';
import {FlowchartConfigMetadata} from './reduceFlowchartConfig';

type SaveHandler = (
  flowchartConfig: FlowchartConfigWithMetadata,
  isBlocking?: boolean,
  persistenceInfo?: PersistenceInfo
) => Promise<null | FlowchartConfigMetadata>;

type SetPendingDraftFlowchartUpdate = null | {
  updater: (
    latestDraftFlowchartConfigAndMetadata: FlowchartConfigWithMetadata
  ) => Promise<FlowchartConfigWithMetadata>;
};

/**
 * This factory for making a saveHandler is used to ensure that the saveHandler is protected from
 * unmounted components.
 *
 * Callers must ensure that the callbacks are noops if the component is unmounted. They are only provided
 * to allow components to do bookkeeping, which isn't needed once the component is unmounted.
 *
 * The factory returns a saveHandler that can be passed to the AutosaveManager.
 */
export function saveHandlerFactory(
  handleSave: (
    snapShotFlowchartConfigWithMetadata: FlowchartConfigWithMetadata,
    context?: PersistenceInfo
  ) => Promise<null | {
    snapshotFlowchartConfigWithMetadata: FlowchartConfigWithMetadataAndRootTaskSchema;
    additionalPersistedData: ProcessFlowchartAdditionalPersistedData;
  }>,
  setPendingDraftFlowchartUpdate: (newUpdate: SetPendingDraftFlowchartUpdate) => Promise<void>,
  processFlowchartConfigId: string
): SaveHandler {
  return (flowchartConfig: FlowchartConfigWithMetadata, isBlocking?: boolean) =>
    saveHandler(flowchartConfig, isBlocking, {
      handleSave,
      setPendingDraftFlowchartUpdate,
      processFlowchartConfigId,
    });
}

interface FactoryOptions {
  handleSave: (
    snapShotFlowchartConfigWithMetadata: FlowchartConfigWithMetadata,
    context?: PersistenceInfo
  ) => Promise<null | {
    snapshotFlowchartConfigWithMetadata: FlowchartConfigWithMetadataAndRootTaskSchema;
    additionalPersistedData: ProcessFlowchartAdditionalPersistedData;
  }>;
  setPendingDraftFlowchartUpdate: (newUpdate: SetPendingDraftFlowchartUpdate) => Promise<void>;
  processFlowchartConfigId: string;
}

async function saveHandler(
  // --- Provided by the caller (i.e., the AutosaveManager)
  // This is coming from FlowchartConfigAutosaveManagerCallbacks.getDataToSave()
  // which pulls from redux store at the start of the save
  snapShotFlowchartConfigWithMetadata: FlowchartConfigWithMetadata,
  isBlocking: boolean = false,
  // --- Provided by the factory
  factoryOptions: FactoryOptions
): Promise<FlowchartConfigMetadata | null> {
  const {handleSave, setPendingDraftFlowchartUpdate, processFlowchartConfigId} = factoryOptions;

  const typedFlowchartConfig = getFlowchartConfigSafely(snapShotFlowchartConfigWithMetadata);

  const flowchartAdapter: ProcessFlowchartAdapter<unknown, unknown> = PLUGIN_REGISTRY_INSTANCE.getNodeAdapter(
    ProcessFlowchartNodeType.TASK_SCHEMA_NODE,
    typedFlowchartConfig.metadata.rootTaskSchema.executionType
  ) as ProcessFlowchartAdapter<unknown, unknown>;
  // Allow flowchart adapter to perform any pre-save work
  flowchartAdapter.onSaveStart(typedFlowchartConfig);

  try {
    // This is calling the DAO functions, etc
    const result = await handleSave(snapShotFlowchartConfigWithMetadata);

    if (result != null) {
      await setPendingDraftFlowchartUpdate({
        updater: async (latestDraftFlowchartConfigAndMetadata: FlowchartConfigWithMetadata) => {
          // latestDraftFlowchartConfigAndMetadata contains any changes made in flight during this save

          const typedLatestDraftFlowchartConfig = getFlowchartConfigSafely(
            latestDraftFlowchartConfigAndMetadata
          );

          // Update the currentVersionId in the draft flowchart config metadata
          const draftFlowchartConfigWithUpdates: FlowchartConfigWithMetadataAndRootTaskSchema = {
            ...typedLatestDraftFlowchartConfig,
            metadata: {
              ...typedLatestDraftFlowchartConfig.metadata,
              currentVersionId: result.additionalPersistedData.currentVersionId,
            },
          };

          // Do any additional modification of the draft flowchart config after the save
          const draftFlowchartConfigWithSaveCompleteUpdates = await flowchartAdapter.onSaveComplete(
            draftFlowchartConfigWithUpdates,
            result.additionalPersistedData
          );
          // If no modifications are needed, return the draft flowchart config with the updates
          return draftFlowchartConfigWithSaveCompleteUpdates ?? draftFlowchartConfigWithUpdates;
        },
      });
    }
    return result?.snapshotFlowchartConfigWithMetadata.metadata ?? null;
  } catch (e) {
    if (e.status === 409) {
      // The update conflicts with the version stored on the server.
      setIsFlowchartConfigAutosaverBlocked(processFlowchartConfigId, true);
      const userMessage = notifications.getFriendlyError(e);
      if (userMessage != null) {
        notifications.error(userMessage, {duration: 0});
      }
      return null;
    } else {
      // Directly dispatch the error state (reducer will automatically set isDirty=true)
      setSaveInFlightWithError(processFlowchartConfigId, false, e);
      throw e;
    }
  } finally {
    // TODO: Add event logging
  }
}
